/**
 * Shared model for the Chapter Progress What's New experience.
 *
 * The presentational component (ChapterProgressView) is data-agnostic: it takes
 * a ready list of ChapterStat. Each host maps its own source into that shape —
 * the student app from its own practice tree, the admin console from any user's
 * tree — via `toChapterStats`, so both render identical results.
 *
 * See docs/FeatureDesigns/whats-new-chapter-progress.md.
 */

/** The subset of a practice-tree node this experience needs. Structurally
 *  compatible with the student app's PracticeNode and the backend's response. */
export interface PracticeTreeNodeLike {
    id: string;
    name: string;
    level: string; // 'unit' | 'chapter' | 'section'
    totalQuestions: number;
    questionsAttempted: number;
    avgCorrectnessScore: number | null;
    avgApproachScore: number | null;
}

export interface ChapterStat {
    id: string;
    name: string;
    /** 0–10 correctness = the accuracy ring. */
    accuracy: number;
    approach: number | null;
    attempted: number;
    total: number;
}

/** Minimum questions attempted for a chapter's accuracy to be trustworthy — a
 *  single 100%-correct question is not a highlight. Chapters below this are
 *  excluded from the Chapter Progress card. */
export const MIN_ATTEMPTS_FOR_STATS = 5;

/** Chapters with enough attempts to matter (>= MIN_ATTEMPTS_FOR_STATS), best
 *  accuracy first. */
export function toChapterStats(nodes: PracticeTreeNodeLike[]): ChapterStat[] {
    return nodes
        .filter(n => n.level === 'chapter' && n.questionsAttempted >= MIN_ATTEMPTS_FOR_STATS)
        .map(n => ({
            id: n.id,
            name: n.name,
            accuracy: n.avgCorrectnessScore ?? 0,
            approach: n.avgApproachScore,
            attempted: n.questionsAttempted,
            total: n.totalQuestions,
        }))
        .sort((a, b) => b.accuracy - a.accuracy);
}

// Accuracy tiers — mirror the student app's mastery tiers (needs-work < 4 ≤
// building < 9 ≤ strong) so the ring colour matches the Subject page.
const BUILDING_MIN = 4;
const STRONG_MIN = 9;
export type AccuracyTier = 'strong' | 'building' | 'needs-work';
export const accuracyTier = (score: number): AccuracyTier =>
    score >= STRONG_MIN ? 'strong' : score >= BUILDING_MIN ? 'building' : 'needs-work';
export const ACCURACY_TIER_COLOR: Record<AccuracyTier, string> = {
    strong: 'var(--mastery-strong)',
    building: 'var(--mastery-building)',
    'needs-work': 'var(--mastery-needs-work)',
};
